import React from 'react';
import {motion} from 'framer-motion';
import Header from '../layout/Header';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Footer from '../sections/Footer';

const {FiHeart,FiUsers,FiShield,FiPhone,FiMail,FiExternalLink,FiAlertTriangle,FiCheck,FiBookOpen,FiEye,FiLock,FiUserCheck,FiGlobe,FiClock,FiTarget,FiZap,FiStar,FiDownload,FiHelpCircle,FiUser,FiInfo,FiAward,FiCalendar,FiMapPin,FiMessageSquare}=FiIcons;

const ScamAssistancePage=()=> {
  const services=[
    {
      icon: FiHeart,
      title: "Victim Advocacy",
      description: "Personalized support with certified advocates specializing in trauma,scams,and identity theft.",
      color: "from-pink-500 to-red-500"
    },
    {
      icon: FiUserCheck,
      title: "NACP Certification Expertise",
      description: "Our team holds specialized certifications in Trauma,Scams,and Identity Theft recovery.",
      color: "from-purple-500 to-blue-500"
    },
    {
      icon: FiBookOpen,
      title: "Educational Outreach",
      description: "Comprehensive workshops and resources to prevent future scam victimization.",
      color: "from-cyan-500 to-teal-500"
    },
    {
      icon: FiLock,
      title: "Personal Information Safeguarding",
      description: "Expert guidance on securing devices,accounts,and personal data from scammers.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: FiZap,
      title: "Scam Interruption",
      description: "Proactive measures to halt ongoing fraudulent activities and report to authorities.",
      color: "from-orange-500 to-yellow-500"
    },
    {
      icon: FiUsers,
      title: "Community Collaboration",
      description: "Strategic partnerships with law enforcement,FBI,and protective services.",
      color: "from-indigo-500 to-purple-500"
    }
  ];

  const testimonials=[
    {
      name: "Sarah M.",
      location: "New York",
      text: "AVAH helped me recover from a devastating romance scam. Their advocates understood my trauma and guided me through every step of reporting and recovery.",
      rating: 5
    },
    {
      name: "Robert K.",
      location: "California",
      text: "After losing $15,000 to a tech support scam,AVAH's team helped me secure my accounts and work with authorities. I felt supported throughout the entire process.",
      rating: 5
    },
    {
      name: "Maria L.",
      location: "Florida",
      text: "The educational workshops opened my eyes to scam tactics I never knew existed. AVAH's prevention programs are invaluable for protecting our community.",
      rating: 5
    }
  ];

  const stats=[
    {
      number: "10,000+",
      label: "Victims Helped"
    },
    {
      number: "95%",
      label: "Success Rate"
    },
    {
      number: "24/7",
      label: "Support Available"
    },
    {
      number: "100%",
      label: "Free Services"
    }
  ];

  const recoverySteps=[
    "Immediate crisis intervention and emotional support",
    "Comprehensive risk assessment of personal information exposure",
    "Coordination with financial institutions to secure accounts",
    "Reporting to appropriate law enforcement agencies",
    "Identity restoration services and credit monitoring",
    "Ongoing advocacy throughout the recovery process",
    "Connection to community resources and support networks"
  ];

  const scamTypes=[
    "Romance scams and online dating fraud",
    "Tech support scams and computer repair fraud",
    "IRS impersonation and government agency scams",
    "Investment scams and Ponzi schemes",
    "Charity fraud and disaster relief scams",
    "Employment and job opportunity scams",
    "Healthcare and medical insurance fraud",
    "Rental and real estate scams",
    "Debt relief and loan modification fraud",
    "Cryptocurrency and digital asset scams",
    "Subscription and free trial scams",
    "Travel and vacation package fraud",
    "Puppy and pet scams",
    "Online dating extortion and blackmail",
    "Fake reviews and ratings manipulation"
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 min-h-screen flex items-center justify-center overflow-hidden">
        {/* NYC Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="New York City Skyline" className="w-full h-full object-cover" />
          {/* Dark gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90 z-10"></div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 z-20">
          <div className="absolute top-20 left-10 w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
          <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-cyan-400 rounded-full animate-pulse"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-30">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{opacity: 0,y: 50}} animate={{opacity: 1,y: 0}} transition={{duration: 0.8}}>
            <motion.h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent" initial={{opacity: 0,scale: 0.8}} animate={{opacity: 1,scale: 1}} transition={{delay: 0.2,duration: 0.8}}>
              Scam Assistance & Recovery
            </motion.h1>
            <motion.p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed font-medium" initial={{opacity: 0}} animate={{opacity: 1}} transition={{delay: 0.4,duration: 0.8}}>
              Professional support for scam victims through the AVAH Outreach Group - where healing begins and protection strengthens.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" initial={{opacity: 0,y: 30}} animate={{opacity: 1,y: 0}} transition={{delay: 0.6,duration: 0.8}}>
              <a href="#contact-section" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3 shadow-lg hover:shadow-pink-500/25">
                <SafeIcon icon={FiHeart} className="text-xl" />
                Get Help Now
              </a>
              <a href="#services" className="bg-transparent border-2 border-cyan-400 hover:bg-cyan-400 hover:text-gray-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3">
                <SafeIcon icon={FiBookOpen} className="text-xl" />
                Learn More
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat,index)=> (
              <motion.div key={stat.label} className="text-center" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{delay: index * 0.1,duration: 0.6}} viewport={{once: true}}>
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <motion.div className="max-w-4xl mx-auto" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Comprehensive Scam Victim Support Services
            </h2>
            
            <div className="bg-gray-800 p-8 rounded-2xl border border-pink-500/30 mb-12">
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                The AVAH Outreach Group provides critical support services to victims of scams and fraud. As certified victim advocates with specialized training in trauma, scams, and identity theft, our team understands the complex emotional and practical challenges victims face. We offer compassionate, professional assistance to help individuals navigate the recovery process and protect themselves from future exploitation.
              </p>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Scams come in many forms - from romance scams that prey on our emotions to tech support frauds that exploit our fears about technology. Regardless of the method, the impact on victims can be devastating. Financial losses are often just the beginning. Many victims experience trauma, depression, anxiety, and a profound sense of violation. AVAH recognizes that recovery requires more than just practical assistance - it requires emotional support, advocacy, and community connection.
              </p>
              
              <div className="flex items-start gap-4 mt-8">
                <SafeIcon icon={FiShield} className="text-pink-500 text-2xl mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">AVAH's Mission</h3>
                  <p className="text-gray-300">
                    To empower scam victims through comprehensive support services while building community resilience against fraudulent activities. Our mission is threefold: to provide immediate crisis intervention, to facilitate long-term recovery, and to prevent future victimization through education and awareness.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-8 rounded-2xl border border-purple-500/30">
                <h3 className="text-2xl font-bold text-white mb-4">Recognizing the Signs of Scam Victimization</h3>
                <p className="text-gray-300 mb-4">
                  Victims of scams often experience a range of indicators that signal they've been targeted:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Unexpected financial losses without clear explanations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">New unauthorized charges on credit cards or bank accounts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Unsolicited contact claiming to be from legitimate organizations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Requests for personal information or remote access to devices</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Feelings of shame, embarrassment, or isolation after online interactions</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-800 p-8 rounded-2xl border border-cyan-500/30">
                <h3 className="text-2xl font-bold text-white mb-4">AVAH Outreach Group Core Services</h3>
                <p className="text-gray-300 mb-4">
                  Our comprehensive support system includes:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">24/7 crisis intervention and emotional support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Risk assessment and personal information protection</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Coordination with financial institutions and credit monitoring</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Reporting to law enforcement and regulatory agencies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Identity restoration and ongoing advocacy services</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About AVAH Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div className="max-w-6xl mx-auto" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
                About AVAH Outreach Group
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Empowering individuals to combat scams and safeguard their digital well-being,the AVAH Outreach Group offers a comprehensive array of services tailored to assist victims of scams and prevent future threats.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{delay: 0.2,duration: 0.8}} viewport={{once: true}}>
                <div className="bg-gray-800 p-8 rounded-2xl border border-pink-500/30">
                  <SafeIcon icon={FiShield} className="text-4xl text-cyan-400 mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    AVAH Outreach Group stands as a beacon of hope for scam victims,providing certified assistance,education,and advocacy to empower individuals and communities in the fight against fraud and exploitation.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-400" />
                      <span className="text-gray-300">100% Free Services</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-400" />
                      <span className="text-gray-300">NACP Certified Advocates</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-400" />
                      <span className="text-gray-300">24/7 Support Available</span>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{delay: 0.4,duration: 0.8}} viewport={{once: true}}>
                <div className="space-y-6">
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiTarget} className="text-2xl text-pink-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Victim-Centered Approach</h4>
                    <p className="text-gray-400 text-sm">
                      Every service is designed with the victim's healing and empowerment at the center,ensuring compassionate and effective support throughout the recovery journey.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiGlobe} className="text-2xl text-purple-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Community Impact</h4>
                    <p className="text-gray-400 text-sm">
                      Through education and awareness campaigns,we strengthen entire communities against scam threats while building networks of informed,vigilant citizens.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiUsers} className="text-2xl text-cyan-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Collaborative Network</h4>
                    <p className="text-gray-400 text-sm">
                      Strategic partnerships with law enforcement,FBI,and protective services ensure comprehensive support and effective scam intervention.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-br from-purple-900 to-gray-900">
        <div className="container mx-auto px-6">
          <motion.div className="text-center mb-16" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Comprehensive Support Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              AVAH Outreach Group provides a complete spectrum of services designed to support scam victims through every stage of recovery and protection.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service,index)=> (
              <motion.div key={service.title} className="group" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{delay: index * 0.1,duration: 0.6}} viewport={{once: true}}>
                <div className="bg-gray-900 p-8 rounded-2xl border border-gray-700 hover:border-pink-500/50 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className={`bg-gradient-to-br ${service.color} p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6`}>
                    <SafeIcon icon={service.icon} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-pink-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-16">
            
            {/* Comprehensive Recovery Process */}
            <motion.div className="bg-gray-900 p-12 rounded-3xl border border-pink-500/30" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <h3 className="text-3xl font-bold text-white mb-8 text-center">AVAH's Comprehensive Recovery Process</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
                <div>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Recovery from scam victimization is a multi-faceted process that extends far beyond simply stopping financial losses. AVAH's comprehensive approach addresses the immediate crisis while building long-term resilience. Our certified advocates guide victims through each stage of recovery with personalized support tailored to their unique circumstances.
                  </p>
                  
                  <p className="text-gray-300 leading-relaxed mb-6">
                    The recovery journey begins with immediate crisis intervention. Our advocates understand that victims often experience a range of emotions from disbelief and denial to shame and anger. We provide a safe, non-judgmental space where victims can process their experiences and begin to develop a recovery plan.
                  </p>
                  
                  <p className="text-gray-300 leading-relaxed">
                    Our structured approach ensures no aspect of recovery is overlooked. From securing compromised accounts to reporting crimes to law enforcement, from accessing emotional support to preventing future victimization, AVAH provides comprehensive guidance throughout the entire process.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {recoverySteps.map((step,index)=> (
                    <div key={index} className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 flex items-start gap-4">
                      <div className="bg-gradient-to-br from-pink-500 to-purple-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      <p className="text-gray-300">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-800/30 p-8 rounded-2xl border border-gray-700">
                <h4 className="text-xl font-bold text-white mb-4">Ongoing Support and Advocacy</h4>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Recovery from scam victimization is not a linear process. AVAH provides ongoing support to ensure victims have resources as they navigate challenges that may arise weeks, months, or even years after the initial incident. Our advocates remain available for follow-up consultations and can help victims address new issues related to their original victimization.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We also provide advocacy support when victims interact with financial institutions, law enforcement, or legal systems. Our advocates can accompany victims to meetings, help them understand their rights, and ensure their voices are heard throughout any proceedings related to their case.
                </p>
              </div>
            </motion.div>
            
            {/* Types of Scams We Address */}
            <motion.div className="bg-gray-900 p-12 rounded-3xl border border-purple-500/30" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <h3 className="text-3xl font-bold text-white mb-8 text-center">Types of Scams Addressed by AVAH</h3>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-4xl mx-auto text-center">
                AVAH Outreach Group provides specialized support for victims of all types of scams. Our advocates stay current with evolving scam tactics to ensure victims receive the most relevant assistance regardless of the method used to target them.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {scamTypes.map((scam,index)=> (
                  <div key={index} className="bg-gray-800/50 p-5 rounded-xl border border-gray-700 flex items-start gap-3">
                    <SafeIcon icon={FiAlertTriangle} className="text-purple-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">{scam}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-br from-gray-800 to-purple-900/50 p-8 rounded-2xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-4">Emerging Scam Trends</h4>
                <p className="text-gray-300 leading-relaxed mb-4">
                  As technology evolves,scammers develop increasingly sophisticated methods to exploit victims. AVAH continuously monitors emerging scam trends to ensure our services remain effective and relevant. Our team participates in regular training updates and collaborates with law enforcement and cybersecurity experts to stay ahead of new tactics.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We specialize in helping victims navigate complex scams that combine multiple tactics,such as romance scams that evolve into investment fraud or tech support scams that incorporate identity theft components. Our comprehensive approach ensures victims receive appropriate support regardless of scam complexity.
                </p>
              </div>
            </motion.div>
            
            {/* Victim Advocacy */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div>
                <div className="bg-gradient-to-br from-pink-500 to-red-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiHeart} className="text-2xl text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6">Victim Advocacy</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Victim advocacy is a crucial component of the AVAH Outreach Group's mission,encompassing a range of services aimed at providing personalized support and assistance to individuals who have fallen victim to scams. At the heart of victim advocacy lies a deep commitment to empowering survivors,validating their experiences,and guiding them through the complexities of recovery.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  AVAH's team of certified victim advocates brings a wealth of expertise in trauma,scams,and identity theft,ensuring that victims receive specialized care tailored to their unique needs. Advocates offer a compassionate ear,empathetic listening,and emotional support to help victims navigate the overwhelming emotions and psychological impact of being scammed.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-2xl border border-pink-500/30">
                <h4 className="text-xl font-bold text-white mb-4">What Victim Advocacy Includes:</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-pink-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Compassionate emotional support and trauma-informed care</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-pink-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Guidance on reporting scams to appropriate authorities</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-pink-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Legal process navigation and rights advocacy</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-pink-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Connection to community resources and support networks</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-pink-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Assistance with financial restitution processes</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* NACP Certification */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="order-2 lg:order-1">
                <div className="bg-gray-800 p-8 rounded-2xl border border-purple-500/30">
                  <h4 className="text-xl font-bold text-white mb-4">NACP Certification Benefits:</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiUserCheck} className="text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Rigorous training in trauma-informed victim support</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiUserCheck} className="text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Specialized knowledge in scam tactics and prevention</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiUserCheck} className="text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Identity theft recovery expertise and protocols</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiUserCheck} className="text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Evidence-based intervention strategies</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiUserCheck} className="text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Ongoing professional development and training</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-purple-500 to-blue-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiUserCheck} className="text-2xl text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6">NACP Certification Expertise</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The AVAH Outreach Group's team of victim advocates distinguishes itself by holding NACP certification in Trauma,Scams,and Identity Theft,underscoring their commitment to excellence in supporting scam victims. This certification signifies rigorous training and expertise in understanding the multifaceted challenges that victims encounter in the aftermath of scams.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  By obtaining specialized knowledge in trauma,scams,and identity theft,these advocates are equipped with the necessary skills to provide comprehensive assistance tailored to each individual's needs. Their expertise ensures that victims receive compassionate and effective support as they navigate the path to recovery and empowerment.
                </p>
              </div>
            </motion.div>

            {/* Educational Outreach */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div>
                <div className="bg-gradient-to-br from-cyan-500 to-teal-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiBookOpen} className="text-2xl text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6">Educational Outreach</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The AVAH Outreach Group is dedicated to not only supporting scam victims but also to preventing future victimization through proactive educational outreach programs. These initiatives serve as a vital tool in raising awareness about the prevalence and tactics of scams while empowering the public with the knowledge and skills to recognize and avoid falling victim to them.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Through a variety of channels such as workshops,seminars,online resources,and community events,the AVAH Outreach Group disseminates valuable information about common scams,red flags to watch out for,and best practices for safeguarding personal and financial information.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-2xl border border-cyan-500/30">
                <h4 className="text-xl font-bold text-white mb-4">Educational Programs Include:</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiBookOpen} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Interactive workshops and seminars</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiBookOpen} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Online educational resources and toolkits</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiBookOpen} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Community awareness events and presentations</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiBookOpen} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Scam alert systems and early warning networks</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiBookOpen} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Digital literacy and cybersecurity training</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personal Information Safeguarding & Scam Interruption */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-16">
            
            {/* Personal Information Safeguarding */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="order-2 lg:order-1">
                <div className="bg-gray-900 p-8 rounded-2xl border border-green-500/30">
                  <h4 className="text-xl font-bold text-white mb-4">Safeguarding Services:</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiLock} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Device security assessment and hardening</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiLock} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Account security review and strengthening</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiLock} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Password management and two-factor authentication setup</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiLock} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Removal of scammer access from devices and accounts</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiLock} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Ongoing monitoring and security maintenance</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiLock} className="text-2xl text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6">Personal Information Safeguarding</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The AVAH Outreach Group takes a proactive approach in assisting victims by actively engaging with them to provide invaluable insights and guidance on safeguarding personal information. Trained advocates understand the urgency of preventing further harm after a scam and work closely with victims to implement protective measures.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  This includes offering step-by-step instructions on how to secure devices and online accounts,such as changing passwords,enabling two-factor authentication,and monitoring for suspicious activity. The organization remains committed to assisting victims in removing scammers' access to their devices and accounts through various intervention methods.
                </p>
              </div>
            </motion.div>

            {/* Scam Interruption */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div>
                <div className="bg-gradient-to-br from-orange-500 to-yellow-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiZap} className="text-2xl text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6">Scam Interruption</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The AVAH Outreach Group stands at the forefront of combating scams by adopting proactive measures to disrupt ongoing fraudulent activities. Understanding the urgent need to halt scams in their tracks,the organization takes swift action to report deceptive financial transactions,fraudulent bank accounts,and other illicit activities to the appropriate authorities.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Armed with a deep understanding of scam tactics and a commitment to protecting vulnerable individuals,AVAH's team of dedicated volunteers works tirelessly to identify and flag suspicious behavior,ensuring that scammers are held accountable for their actions.
                </p>
              </div>
              <div className="bg-gray-900 p-8 rounded-2xl border border-orange-500/30">
                <h4 className="text-xl font-bold text-white mb-4">Interruption Methods:</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiZap} className="text-orange-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Real-time scam detection and reporting</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiZap} className="text-orange-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Financial institution fraud alerts</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiZap} className="text-orange-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Law enforcement coordination and reporting</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiZap} className="text-orange-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Regulatory body notifications</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiZap} className="text-orange-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Scammer operation disruption</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Collaboration & Community Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div className="text-center mb-16" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Collaboration & Community Impact
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Building stronger communities through strategic partnerships and collective action against scams.
            </p>
          </motion.div>
          
          <div className="max-w-6xl mx-auto space-y-16">
            
            {/* Collaboration */}
            <motion.div className="bg-gray-900 p-12 rounded-3xl border border-purple-500/30" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                    <SafeIcon icon={FiUsers} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-6">Strategic Partnerships</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    The AVAH Outreach Group operates within a collaborative framework,recognizing the importance of partnerships in effectively addressing the scourge of scams. This commitment to collaboration extends to various stakeholders,including law enforcement agencies,the FBI,adult protective services,victims' families,and other institutions dedicated to protecting vulnerable individuals.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    By forging strategic alliances with these key partners,AVAH ensures a comprehensive and coordinated approach to combating scams at every level,maximizing impact and creating resilient communities.
                  </p>
                </div>
                <div className="space-y-6">
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiShield} className="text-2xl text-indigo-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Law Enforcement Collaboration</h4>
                    <p className="text-gray-400 text-sm">
                      Direct partnerships with police departments,FBI,and federal agencies to investigate and prosecute scammers while supporting victims through the process.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiHeart} className="text-2xl text-pink-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Family Support Networks</h4>
                    <p className="text-gray-400 text-sm">
                      Engaging with victims' families to provide critical support and advocacy,fostering supportive networks that enhance overall well-being and recovery.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiUsers} className="text-2xl text-purple-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Protective Services</h4>
                    <p className="text-gray-400 text-sm">
                      Coordination with adult protective services to ensure vulnerable populations receive necessary protection and support to prevent exploitation.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Community Awareness */}
            <motion.div className="bg-gray-900 p-12 rounded-3xl border border-cyan-500/30" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="order-2 lg:order-1 space-y-6">
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiGlobe} className="text-2xl text-cyan-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Information Sharing Networks</h4>
                    <p className="text-gray-400 text-sm">
                      Facilitating the sharing of scam alerts,prevention tips,and real experiences through social media,forums,and community platforms.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiBookOpen} className="text-2xl text-teal-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Educational Workshops</h4>
                    <p className="text-gray-400 text-sm">
                      Organizing community events that provide opportunities for open dialogue,knowledge-sharing,and collective learning about scam prevention.
                    </p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiTarget} className="text-2xl text-green-500 mb-3" />
                    <h4 className="font-bold text-white mb-2">Advocacy Initiatives</h4>
                    <p className="text-gray-400 text-sm">
                      Encouraging community members to advocate for stronger consumer protection measures at local and national levels while promoting vigilance.
                    </p>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="bg-gradient-to-br from-cyan-500 to-teal-500 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                    <SafeIcon icon={FiGlobe} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-6">Community Awareness Campaigns</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Encouraging community involvement is a cornerstone of the AVAH Outreach Group's mission,as they firmly believe that collective action is key to combating scams effectively. By fostering a sense of shared responsibility and empowerment within communities,AVAH aims to mobilize individuals to take an active role in the fight against scams.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Through various outreach initiatives and awareness campaigns,the organization encourages community members to share information,personal experiences,and insights related to scams,creating a culture of collaboration,transparency,and empowerment.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 to-gray-900">
        <div className="container mx-auto px-6">
          <motion.div className="text-center mb-16" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Real Stories,Real Recovery
            </h2>
            <p className="text-xl text-gray-300">
              Hear from those who found hope and healing through AVAH's support services.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial,index)=> (
              <motion.div key={testimonial.name} className="bg-gray-800 p-8 rounded-2xl border border-gray-700 hover:border-pink-500/50 transition-all duration-300" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{delay: index * 0.2,duration: 0.6}} viewport={{once: true}}>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_,i)=> (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-300 italic mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-bold text-white">{testimonial.name}</div>
                  <div className="text-gray-400 text-sm">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Services Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <div className="bg-gray-900 p-12 rounded-3xl border border-pink-500/30 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-40 h-40 bg-pink-500/5 rounded-full -translate-y-20 -translate-x-20"></div>
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-cyan-400/5 rounded-full translate-y-16 translate-x-16"></div>
              
              <motion.div initial={{opacity: 0,scale: 0.8}} whileInView={{opacity: 1,scale: 1}} transition={{delay: 0.2,duration: 0.6}} viewport={{once: true}}>
                <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <SafeIcon icon={FiHeart} className="text-3xl text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
                  100% Free of Charge
                </h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  The AVAH Outreach Group operates on a selfless ethos,exemplifying a commitment to serving scam victims and their communities without seeking financial gain. Central to their mission is the principle of providing all services completely free of charge,ensuring that support and assistance are accessible to all individuals affected by scams.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiCheck} className="text-2xl text-green-400 mb-3" />
                    <h3 className="font-bold text-white mb-2">No Hidden Fees</h3>
                    <p className="text-gray-400 text-sm">Every service,consultation,and resource is provided at absolutely no cost to victims</p>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <SafeIcon icon={FiUsers} className="text-2xl text-purple-500 mb-3" />
                    <h3 className="font-bold text-white mb-2">Community Driven</h3>
                    <p className="text-gray-400 text-sm">Powered by volunteers and funded through donations to maintain free access for all</p>
                  </div>
                </div>
                
                <p className="text-gray-300 leading-relaxed mb-8">
                  By offering their services at no cost,AVAH removes barriers to access and ensures that victims receive the help they need without any financial burden. The organization asks for nothing in return from victims except for the sharing of information to help build a stronger,more informed community.
                </p>
                
                <a href="https://avahoutreach.org/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3 shadow-lg">
                  <SafeIcon icon={FiExternalLink} className="text-xl" />
                  Get Free Help Today
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <motion.div className="max-w-6xl mx-auto" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Additional Resources for Scam Victims
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div className="bg-gray-800 p-8 rounded-2xl border border-pink-500/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <SafeIcon icon={FiBookOpen} className="text-pink-500" />
                  Educational Materials
                </h3>
                
                <p className="text-gray-300 mb-6">
                  AVAH Outreach Group provides a comprehensive library of educational resources to help victims understand their situation and learn protective strategies:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiDownload} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Recovery Guide</h4>
                      <p className="text-gray-400 text-sm">Step-by-step instructions for securing accounts and beginning recovery</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiDownload} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Prevention Toolkit</h4>
                      <p className="text-gray-400 text-sm">Strategies and checklists for avoiding future scams</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiDownload} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Scam Recognition Manual</h4>
                      <p className="text-gray-400 text-sm">Detailed descriptions of common scam tactics and red flags</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiDownload} className="text-cyan-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Legal Rights Handbook</h4>
                      <p className="text-gray-400 text-sm">Information about victim rights and legal protections</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 p-8 rounded-2xl border border-purple-500/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <SafeIcon icon={FiUsers} className="text-purple-500" />
                  Support Networks
                </h3>
                
                <p className="text-gray-300 mb-6">
                  Recovery is strengthened through community connection. AVAH facilitates access to various support networks:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiUsers} className="text-purple-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Peer Support Groups</h4>
                      <p className="text-gray-400 text-sm">Connect with others who understand your experience in a safe environment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiUsers} className="text-purple-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Family Support Resources</h4>
                      <p className="text-gray-400 text-sm">Guidance for family members supporting scam victims</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiUsers} className="text-purple-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Professional Counseling</h4>
                      <p className="text-gray-400 text-sm">Referrals to trauma-informed mental health professionals</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiUsers} className="text-purple-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Community Forums</h4>
                      <p className="text-gray-400 text-sm">Online spaces for sharing experiences and advice</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-12 rounded-3xl border border-cyan-500/30">
              <h3 className="text-3xl font-bold text-white mb-8 text-center">Understanding the Scam Recovery Journey</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-cyan-500 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-4">Recognition Phase</h4>
                  <p className="text-gray-300">
                    Victims often begin to recognize they've been scammed through financial alerts, suspicious activity, or information from AVAH advocates.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-4">Action Phase</h4>
                  <p className="text-gray-300">
                    With AVAH support, victims take concrete steps to secure accounts, report incidents, and begin recovery processes.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-gradient-to-br from-pink-500 to-red-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-4">Recovery Phase</h4>
                  <p className="text-gray-300">
                    Ongoing support helps victims process emotional impacts, rebuild confidence, and develop long-term protective strategies.
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-900/50 p-8 rounded-2xl border border-gray-700">
                <h4 className="text-xl font-bold text-white mb-4">The Role of Advocacy in Long-term Recovery</h4>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Beyond immediate crisis intervention, AVAH provides ongoing advocacy that supports long-term recovery. This includes helping victims navigate complex legal and financial systems, connecting them with appropriate resources, and ensuring their voices are heard when dealing with institutions.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Our advocates understand that recovery is not a linear process and remain available for follow-up support as victims face new challenges or opportunities in their journey. We also work to identify systemic issues in how institutions respond to scam victims and advocate for policy changes that improve outcomes for future victims.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact & Resources Section */}
      <section id="contact-section" className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div className="text-center mb-16" initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Get Help Today
            </h2>
            <p className="text-xl text-gray-300">
              Don't face scam recovery alone. AVAH is here to support you every step of the way.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="bg-gray-900 p-8 rounded-2xl border border-pink-500/30">
                <h3 className="text-2xl font-bold text-white mb-6">Emergency Support</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-500 p-3 rounded-lg flex-shrink-0">
                      <SafeIcon icon={FiPhone} className="text-xl text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Online Support</h4>
                      <p className="text-gray-400 text-sm mb-2">Available 24/7 for immediate assistance</p>
                      <a href="https://avahoutreach.org/" target="_blank" rel="noopener noreferrer" className="text-pink-400 font-medium hover:underline">Visit AVAH Outreach Website</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                      <SafeIcon icon={FiMail} className="text-xl text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Email Support</h4>
                      <p className="text-gray-400 text-sm mb-2">Secure communication for sensitive information</p>
                      <a href="https://avahoutreach.org/contact-avah-outreach-group/" target="_blank" rel="noopener noreferrer" className="text-purple-400 font-medium hover:underline">Contact AVAH Support Team</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-500 p-3 rounded-lg flex-shrink-0">
                      <SafeIcon icon={FiClock} className="text-xl text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Response Time</h4>
                      <p className="text-gray-400 text-sm">Online requests answered promptly</p>
                      <p className="text-gray-400 text-sm">Email responses within 24-48 hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
              <div className="bg-gray-900 p-8 rounded-2xl border border-cyan-500/30">
                <h3 className="text-2xl font-bold text-white mb-6">Additional Resources</h3>
                <div className="space-y-4">
                  <a href="https://avahoutreach.org/" target="_blank" rel="noopener noreferrer" className="block bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 hover:border-pink-500/50">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiExternalLink} className="text-pink-500" />
                      <div>
                        <div className="font-bold text-white">AVAH Official Website</div>
                        <div className="text-gray-400 text-sm">Complete resources and support portal</div>
                      </div>
                    </div>
                  </a>
                  <a href="http://link.lrcs.nyc/YTSL" target="_blank" rel="noopener noreferrer" className="block bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 hover:border-purple-500/50">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiEye} className="text-purple-500" />
                      <div>
                        <div className="font-bold text-white">Linda Roberts YouTube</div>
                        <div className="text-gray-400 text-sm">Educational content and scam awareness</div>
                      </div>
                    </div>
                  </a>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiBookOpen} className="text-cyan-500" />
                      <div>
                        <div className="font-bold text-white">Free Recovery Guide</div>
                        <div className="text-gray-400 text-sm">Step-by-step recovery instructions</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiUsers} className="text-green-500" />
                      <div>
                        <div className="font-bold text-white">Support Groups</div>
                        <div className="text-gray-400 text-sm">Connect with other survivors</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <div className="container mx-auto px-6 text-center">
          <motion.div className="max-w-4xl mx-auto" initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}}>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
              Your Recovery Starts Today
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              In summary,the AVAH Group stands as a beacon of support for scam victims,offering certified assistance,education,and advocacy to empower individuals and fight against scams. Through their dedication and compassion,they not only help victims rebuild their lives but also work towards a future where scams have no place in our communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://avahoutreach.org/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-3 shadow-lg justify-center">
                <SafeIcon icon={FiHeart} className="text-2xl" />
                Start Your Recovery Journey
              </a>
            </div>
            <p className="text-gray-400 mt-6">
              Remember: You are not alone. Help is available,and recovery is possible.
            </p>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ScamAssistancePage;